/**
 * MarqueeText — auto-scrolling overflow text.
 * If the text fits within its container, it renders statically.
 * If it overflows, it horizontally scrolls left at `speedPxPerSec`,
 * with a small pause at each end so it never feels jarring.
 *
 * Use this anywhere a track / artist / title string might overflow
 * (radio player, mini-player, etc.) instead of `truncate`.
 */
import { useEffect, useRef, useState } from 'react';

export function MarqueeText({
  text,
  speedPxPerSec = 40,
  pauseMs = 1200,
  className = '',
  style = {},
  ...rest
}) {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [overflow, setOverflow] = useState(0); // 0 = fits, >0 = px to scroll

  // Measure once + on resize
  useEffect(() => {
    const measure = () => {
      const c = containerRef.current;
      const t = textRef.current;
      if (!c || !t) return;
      const diff = t.scrollWidth - c.clientWidth;
      setOverflow(diff > 4 ? diff : 0);
    };
    measure();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      if (containerRef.current) ro.observe(containerRef.current);
      if (textRef.current) ro.observe(textRef.current);
    } else {
      window.addEventListener('resize', measure);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', measure);
    };
  }, [text]);

  // Build a keyframe duration based on overflow distance + speed.
  // Roundtrip: 0 → -overflow → 0 with pauses at each end.
  const scrollDurMs = overflow ? Math.max(2400, (overflow / speedPxPerSec) * 1000) : 0;
  const totalDurMs = scrollDurMs + pauseMs * 2;

  return (
    <div
      ref={containerRef}
      className={`overflow-hidden whitespace-nowrap ${className}`}
      style={style}
      title={text}
      {...rest}
    >
      <span
        ref={textRef}
        className="inline-block"
        style={
          overflow
            ? {
                animation: `mq-scroll ${totalDurMs}ms ease-in-out infinite alternate`,
                // Expose the overflow distance to the keyframe rule
                ['--mq-shift']: `-${overflow}px`,
                willChange: 'transform',
              }
            : undefined
        }
      >
        {text}
      </span>
      {/* Inject keyframes once per mounted instance — cheap and avoids a
          global stylesheet dependency. Pause-scroll-pause-snap loop. */}
      {overflow > 0 && (
        <style>{`
          @keyframes mq-scroll {
            0%, 15%   { transform: translateX(0); }
            85%, 100% { transform: translateX(var(--mq-shift)); }
          }
        `}</style>
      )}
    </div>
  );
}

export default MarqueeText;
